<template>
  <div>
    <sidebar></sidebar>
    <div :style="{ 'margin-left': sidebarwidth }">
      <div class="max-w-2xl mx-auto px-5 py-8">

        <h2 class="text-2xl font-bold mb-1">Register New User</h2>
        <p class="text-sm text-gray-500 mb-6">Create a new system user and assign a role.</p>

        <!-- Success banner -->
        <div v-if="successMsg" class="mb-5 bg-green-50 border border-green-200 text-green-700 rounded px-4 py-3 text-sm flex items-center justify-between">
          <span>✓ {{ successMsg }}</span>
          <button @click="successMsg = ''" class="text-green-500 hover:text-green-700 ml-4 font-bold">✕</button>
        </div>

        <!-- Error banner -->
        <div v-if="errorMsg" class="mb-5 bg-red-50 border border-red-200 text-red-700 rounded px-4 py-3 text-sm flex items-center justify-between">
          <span>{{ errorMsg }}</span>
          <button @click="errorMsg = ''" class="text-red-500 hover:text-red-700 ml-4 font-bold">✕</button>
        </div>

        <div class="bg-white rounded-lg shadow p-6">
          <div class="grid grid-cols-2 gap-5">

            <!-- Email -->
            <div class="col-span-2">
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Email <span class="text-red-500">*</span>
              </label>
              <input
                v-model="formData.email"
                type="email"
                placeholder="user@company.com"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <!-- Password -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Password <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <input
                  v-model="formData.password"
                  :type="showPassword ? 'text' : 'password'"
                  placeholder="Min 8 characters"
                  class="w-full border border-gray-300 rounded px-3 py-2 pr-20 text-sm focus:outline-none focus:border-blue-500"
                />
                <button
                  type="button"
                  @click="showPassword = !showPassword"
                  class="absolute inset-y-0 right-0 px-3 text-xs font-semibold text-gray-600 hover:text-gray-900"
                >
                  {{ showPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
            </div>

            <!-- Confirm Password -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password <span class="text-red-500">*</span>
              </label>
              <div class="relative">
                <input
                  v-model="formData.password_confirm"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  placeholder="Re-enter password"
                  class="w-full border border-gray-300 rounded px-3 py-2 pr-20 text-sm focus:outline-none focus:border-blue-500"
                  :class="!passwordsMatch && formData.password_confirm ? 'border-red-400' : ''"
                />
                <button
                  type="button"
                  @click="showConfirmPassword = !showConfirmPassword"
                  class="absolute inset-y-0 right-0 px-3 text-xs font-semibold text-gray-600 hover:text-gray-900"
                >
                  {{ showConfirmPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
              <p v-if="!passwordsMatch && formData.password_confirm" class="text-red-500 text-xs mt-1">
                Passwords do not match
              </p>
            </div>

            <!-- First Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <input
                v-model="formData.first_name"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <!-- Last Name -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <input
                v-model="formData.last_name"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <!-- Phone -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Phone</label>
              <input
                v-model="formData.phone"
                type="tel"
                placeholder="e.g. 01700000000"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <!-- Department -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <input
                v-model="formData.department"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <!-- Designation -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Designation</label>
              <input
                v-model="formData.designation"
                type="text"
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500"
              />
            </div>

            <!-- Role -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-1">Role <span class="text-red-500">*</span></label>
              <select
                v-model="formData.role"
                required
                class="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500 bg-white"
              >
                <option value="" disabled>Select a role</option>
                <option v-for="r in roleData" :key="r.id" :value="r.id">
                  {{ r.role_name }}
                </option>
              </select>
            </div>

            <!-- Mobile user -->
            <div class="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="is_mobile" v-model="formData.is_mobile" class="cursor-pointer" />
              <label for="is_mobile" class="text-sm text-gray-700 cursor-pointer">
                Mobile user (can log in from mobile app)
              </label>
            </div>

          </div>

          <!-- Actions -->
          <div class="flex justify-end gap-3 mt-6 pt-5 border-t">
            <button
              type="button"
              @click="resetForm"
              class="px-4 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50"
            >Clear</button>
            <button
              type="button"
              @click="submitForm"
              :disabled="loading || !passwordsMatch"
              class="px-5 py-2 rounded text-sm text-white font-semibold bg-black hover:bg-gray-800 disabled:opacity-60"
            >{{ loading ? 'Creating...' : 'Create User' }}</button>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import sidebar from "@/components/sidebar/Sidebar.vue";
import { ref, computed, onMounted } from "vue";
import api from "../../boot/axios.js";
import { sidebarwidth } from "@/store";

const roleData   = ref([]);
const loading    = ref(false);
const successMsg = ref("");
const errorMsg   = ref("");
const showPassword = ref(false);
const showConfirmPassword = ref(false);

const formData = ref(emptyForm());

function emptyForm() {
  return {
    email:            "",
    password:         "",
    password_confirm: "",
    first_name:       "",
    last_name:        "",
    phone:            "",
    designation:      "",
    department:       "",
    role:             "",
    is_mobile:        false,
  };
}

const passwordsMatch = computed(() =>
  !formData.value.password_confirm ||
  formData.value.password === formData.value.password_confirm
);

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim());

// Fetch available roles for the dropdown
const fetchRoles = async () => {
  try {
    const res = await api.get("/auth/role/");
    roleData.value = Array.isArray(res.data) ? res.data : [];
  } catch {
    errorMsg.value = "Failed to load roles. Please refresh.";
  }
};

onMounted(fetchRoles);

const resetForm = () => {
  formData.value = emptyForm();
  successMsg.value = "";
  errorMsg.value   = "";
  showPassword.value = false;
  showConfirmPassword.value = false;
};

const submitForm = async () => {
  successMsg.value = "";
  errorMsg.value   = "";

  // Validations
  if (!formData.value.email)    { errorMsg.value = "Email is required."; return; }
  if (!isValidEmail(formData.value.email)) {
    errorMsg.value = "Please enter a valid email address.";
    return;
  }
  if (!formData.value.role) {
    errorMsg.value = "Role is required.";
    return;
  }
  if (!formData.value.password) { errorMsg.value = "Password is required."; return; }
  if (formData.value.password !== formData.value.password_confirm) {
    errorMsg.value = "Passwords do not match."; return;
  }
  if (formData.value.password.length < 8) {
    errorMsg.value = "Password must be at least 8 characters."; return;
  }

  loading.value = true;
  try {
    // Correct endpoint: POST /auth/user/ (SuperAdmin create user)
    const res = await api.post("/auth/user/", formData.value);

    if (res.status === 201 && res.data.status === true) {
      successMsg.value = `User "${formData.value.email}" created successfully.`;
      resetForm();
      fetchRoles(); // keep dropdown fresh
    } else {
      errorMsg.value = res.data.message || "Failed to create user.";
    }
  } catch (err) {
    if (err?.response?.data) {
      // DRF validation errors — flatten all field errors into one string
      const data = err.response.data;
      errorMsg.value = Object.entries(data)
        .map(([field, msgs]) => {
          const label = field === "non_field_errors" ? "" : `${field}: `;
          return label + (Array.isArray(msgs) ? msgs.join(", ") : msgs);
        })
        .join(" | ");
    } else {
      errorMsg.value = "Something went wrong. Please try again.";
    }
  } finally {
    loading.value = false;
  }
};
</script>